import { useEffect, useRef, useState } from 'react'
import { PlayCircle } from 'lucide-react'

type VideoLang = 'en' | 'ta'

const videos: Record<VideoLang, { label: string; src: string; poster: string; title: string }> = {
  en: {
    label: 'English',
    src: '/videos/sssdental-promo-en.mp4',
    poster: '/videos/sssdental-promo-en.jpg',
    title: 'Sri Sai Sabari Dental Clinic - clinic introduction video (English)'
  },
  ta: {
    label: 'தமிழ்',
    src: '/videos/sssdental-promo-ta.mp4',
    poster: '/videos/sssdental-promo-ta.jpg',
    title: 'ஸ்ரீ சாய் சபரி டென்டல் கிளினிக் - அறிமுக வீடியோ (தமிழ்)'
  }
}

interface ClinicVideoProps {
  heading?: string
  subheading?: string
}

export function ClinicVideo({
  heading = 'Get to Know Our Clinic',
  subheading = 'A two-minute look at Dr. Aishwarya V, our treatments and why patients trust us - in English and Tamil'
}: ClinicVideoProps) {
  const [lang, setLang] = useState<VideoLang>('en')
  const videoRef = useRef<HTMLVideoElement>(null)
  const current = videos[lang]

  // keep playing when switching language mid-video
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const wasPlaying = video.dataset.playing === 'true'
    video.load()
    if (wasPlaying) void video.play().catch(() => {})
  }, [lang])

  return (
    <section className="py-16 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-10 left-1/4 w-64 h-64 bg-gradient-to-br from-yellow-400/10 via-yellow-500/5 to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 right-1/4 w-48 h-48 bg-gradient-to-br from-yellow-500/10 via-yellow-600/5 to-transparent rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <PlayCircle className="hidden sm:block h-8 w-8 text-yellow-500" />
            {heading}
          </h2>
          <p className="text-lg text-gray-300">{subheading}</p>
        </div>

        <div className="flex justify-center mb-6" role="tablist" aria-label="Video language">
          <div className="inline-flex rounded-full bg-white/10 p-1 border border-yellow-500/30">
            {(Object.keys(videos) as VideoLang[]).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={lang === key}
                onClick={() => setLang(key)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  lang === key
                    ? 'bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {videos[key].label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border-2 border-yellow-500/40 shadow-2xl shadow-yellow-500/10 bg-black aspect-video">
          <video
            ref={videoRef}
            className="w-full h-full"
            controls
            playsInline
            preload="none"
            poster={current.poster}
            title={current.title}
            onPlay={(e) => (e.currentTarget.dataset.playing = 'true')}
            onPause={(e) => (e.currentTarget.dataset.playing = 'false')}
          >
            <source src={current.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>
  )
}
